/**
 * `/__ui/timeline` — the three round-5v renderers on the **in-memory** fixture source, with no
 * session, no api and no database (B4 §5's trick, the same one `/__ui/views` uses).
 *
 * It exists so `tools/browser/verify-views-gantt-local.mjs` can hit-test the measured geometry
 * on a served build: a 36 px row, a 20 px bar 8 px inside it, a 48 px group header, a 416 px
 * side panel, a 23 px milestone diamond, an 80 px workload row with a 30 px avatar at x + 40.
 * A box is not a render — every claim goes through `elementFromPoint` — and that needs a page.
 *
 *   `?screen=gantt` (default) · `timeline` · `workload`
 *   `?zoom=days|weeks|months|quarters|years` · `?deps=1` · `?milestone=1` · `?capacity=4`
 */
import {
  DEFAULT_GANTT_VIEW_SETTINGS,
  DEFAULT_TIMELINE_VIEW_SETTINGS,
  DEFAULT_WORKLOAD_VIEW_SETTINGS,
  type GanttViewSettings,
  type TimelineZoom,
  type WorkloadViewSettings,
} from '@elizmonday/shared';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { createFixtureDataSource, FIXTURE_BOARD_ID, FIXTURE_VIEW_ID } from '../../board-view/data/fixture';
import { GanttView } from './GanttView';
import { TimelineView } from '../timeline/TimelineView';
import { WorkloadView } from '../workload/WorkloadView';

/** Frozen so the today line and the fitted range are the same in every run. */
export const HARNESS_TODAY = '2026-03-20';

export function TimelineHarness() {
  const [params] = useSearchParams();
  const screen = params.get('screen') ?? 'gantt';
  const zoom = (params.get('zoom') ?? 'days') as TimelineZoom;
  const deps = params.get('deps') !== null;
  const capacity = params.get('capacity') ?? '';
  const source = useMemo(() => createFixtureDataSource(), []);

  const [gantt, setGantt] = useState<GanttViewSettings>({
    ...DEFAULT_GANTT_VIEW_SETTINGS,
    dateColumns: { all: false, byColumn: { timeline: true } },
    groupBy: 'group',
    labelBy: 'name',
    zoom,
    showDependencies: deps,
  });
  const [timeline, setTimeline] = useState<GanttViewSettings>({
    ...DEFAULT_TIMELINE_VIEW_SETTINGS,
    dateColumns: { all: false, byColumn: { timeline: true } },
    groupBy: 'group',
    zoom,
  });
  const [workload, setWorkload] = useState<WorkloadViewSettings>({
    ...DEFAULT_WORKLOAD_VIEW_SETTINGS,
    resourceBy: 'people',
    dateColumns: { all: false, byColumn: { timeline: true } },
    defaultCapacity: capacity,
    bucket: 'week',
  });

  const boardId = String(FIXTURE_BOARD_ID);
  const viewId = String(FIXTURE_VIEW_ID);

  return (
    <div className="ez-board-host" data-testid={`timeline-harness-${screen}`} style={{ height: '100vh' }}>
      {screen === 'timeline' ? (
        <TimelineView
          boardId={boardId}
          viewId={viewId}
          source={source}
          today={HARNESS_TODAY}
          settingsOverride={{ settings: timeline, save: (patch) => setTimeline((previous) => ({ ...previous, ...patch })) }}
        />
      ) : screen === 'workload' ? (
        <WorkloadView
          boardId={boardId}
          viewId={viewId}
          source={source}
          today={HARNESS_TODAY}
          settingsOverride={{ settings: workload, save: (patch) => setWorkload((previous) => ({ ...previous, ...patch })) }}
        />
      ) : (
        <GanttView
          boardId={boardId}
          viewId={viewId}
          source={source}
          kind="gantt"
          today={HARNESS_TODAY}
          settingsOverride={{ settings: gantt, save: (patch) => setGantt((previous) => ({ ...previous, ...patch })) }}
        />
      )}
    </div>
  );
}
